import overworldTileset from './overworldTileset';

export function parseTilesetConfig(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

export function getDefaultTerrainRenderMode(terrainId) {
  const id = Number(terrainId);
  const map = {
    0: 'fixed',
    1: 'autotile',
    2: 'autotile',
    3: 'autotile',
    4: 'autotile',
    5: 'autotile',
    6: 'autotile',
    7: 'autotile',
    8: 'fixed',
    9: 'fixed',
    10: 'fixed',
    11: 'fixed',
    12: 'fixed',
    13: 'fixed',
    14: 'fixed'
  };
  return map[id] || null;
}

export function normalizeTerrainRenderMode(terrain) {
  const canonical = getDefaultTerrainRenderMode(terrain?.terrainId);
  if (canonical) return canonical;
  if (terrain?.renderMode === 'autotile') return 'autotile';
  if (terrain?.renderMode === 'hybrid') return 'hybrid';
  if (terrain?.autotile) return 'autotile';
  if (Array.isArray(terrain?.cells) && terrain.cells.length > 0) return 'hybrid';
  return 'fixed';
}

export function resolveTilesetImageSrc(image, apiBaseUrl = '') {
  if (!image) return '/assets/tiles/tiles_v2.png';
  if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('blob:')) return image;
  if (image.startsWith('/uploads/')) return `${apiBaseUrl}${image}`;
  return image;
}

export function normalizeTilesetConfig(input) {
  const parsed = parseTilesetConfig(input);
  const base = {
    ...overworldTileset,
    ...(parsed || {}),
    terrains: []
  };

  const terrains = Array.isArray(parsed?.terrains) && parsed.terrains.length > 0
    ? parsed.terrains
    : overworldTileset.terrains;

  base.terrains = terrains.map((terrain) => ({
    terrainId: Number(terrain.terrainId),
    name: '',
    walkable: true,
    autotile: false,
    renderMode: 'fixed',
    autotileRow: 0,
    maskMap: 'default',
    frame: { x: 0, y: 0 },
    cells: [],
    ...terrain,
    renderMode:
      getDefaultTerrainRenderMode(terrain.terrainId) ||
      terrain.renderMode ||
      (terrain.autotile ? 'autotile' : (Array.isArray(terrain.cells) && terrain.cells.length > 0 ? 'hybrid' : 'fixed')),
    autotileRow: Number(terrain.autotileRow || 0),
    frame: {
      x: Number(terrain.frame?.x || 0),
      y: Number(terrain.frame?.y || 0)
    },
    cells: Array.isArray(terrain.cells)
      ? terrain.cells.map((cell) => ({
          x: Number(cell.x || 0),
          y: Number(cell.y || 0)
        }))
      : []
  }));

  base.tileSize = Number(base.tileSize || 48);
  base.renderSize = Number(base.renderSize || 32);
  base.columns = Number(base.columns || 16);
  return base;
}

export function resolveTerrainFrame(terrain, tileSize = 48) {
  if (terrain?.cells?.length > 0) {
    return {
      x: Number(terrain.cells[0]?.x || 0) * tileSize,
      y: Number(terrain.cells[0]?.y || 0) * tileSize
    };
  }

  return {
    x: Number(terrain?.frame?.x || 0) * tileSize,
    y: Number(terrain?.frame?.y || 0) * tileSize
  };
}

export function terrainHasCompatibleNeighbor(terrain, currentTerrainType, neighborTerrainType, terrainById = null) {
  if (neighborTerrainType === undefined || neighborTerrainType === null) return false;
  if (Number(neighborTerrainType) === Number(currentTerrainType)) return true;

  const maskMap = terrain?.maskMap || 'default';
  const neighborTerrain = terrainById?.get?.(Number(neighborTerrainType));
  const neighborMaskMap = neighborTerrain?.maskMap || 'default';
  const terrainId = Number(neighborTerrainType);

  const compatibleGroups = {
    water: ['water', 'lake'],
    lake: ['water', 'lake'],
    beach: ['beach', 'water', 'lake'],
    road: ['road'],
    wall: ['wall'],
    forest: ['forest'],
    swamp: ['swamp', 'water']
  };

  if (compatibleGroups[maskMap]?.includes(neighborMaskMap)) return true;

  const terrainGroups = {
    water: [2, 4, 11],
    road: [1, 6, 9, 12],
    wall: [3, 14],
    forest: [5, 13],
    swamp: [7],
    beach: [10],
    lake: [11]
  };

  if (terrainGroups[maskMap]?.includes(terrainId)) return true;

  return false;
}

export function calculateTerrainBitmask({
  x,
  y,
  layer,
  terrainType,
  currentTiles,
  width,
  height,
  terrainById,
  getNeighborTerrainType = null
}) {
  let mask = 0;
  const terrain = terrainById?.get?.(Number(terrainType));

  const addIfCompatible = (nx, ny, bit) => {
    if (nx < 0 || ny < 0 || nx >= width || ny >= height) {
      mask += bit;
      return;
    }

    const neighborTerrainType = getNeighborTerrainType
      ? getNeighborTerrainType(nx, ny, layer)
      : currentTiles.find((t) => Number(t.coordinate) === Number(ny * width + nx) && Number(t.layer) === Number(layer))?.terrain_type;

    if (terrainHasCompatibleNeighbor(terrain, terrainType, neighborTerrainType, terrainById)) {
      mask += bit;
    }
  };

  addIfCompatible(x, y - 1, 1);
  addIfCompatible(x + 1, y, 2);
  addIfCompatible(x, y + 1, 4);
  addIfCompatible(x - 1, y, 8);

  return mask;
}

export function resolveTerrainSourceRect({
  terrain,
  terrainType,
  x,
  y,
  layer = 0,
  tileSize = 48,
  renderMode = null,
  variantMode = 'auto',
  variantIndex = null,
  forcedVariantCell = null,
  variantCells = null,
  currentTiles = [],
  width = 0,
  height = 0,
  terrainById = null,
  getNeighborTerrainType = null,
  variantSeed = 0
}) {
  const resolvedTerrain = terrainById?.get?.(Number(terrainType)) || terrain;
  if (!resolvedTerrain) return { x: 0, y: 0 };

  const cells = Array.isArray(variantCells)
    ? variantCells
    : Array.isArray(resolvedTerrain?.cells)
      ? resolvedTerrain.cells
      : [];

  if (forcedVariantCell && terrainType !== undefined && terrainType !== null) {
    return {
      x: Number(forcedVariantCell.x || 0) * tileSize,
      y: Number(forcedVariantCell.y || 0) * tileSize
    };
  }

  if (variantMode === 'manual' && variantIndex !== null && variantIndex !== undefined && cells[Number(variantIndex)]) {
    const forced = cells[Number(variantIndex)];
    return {
      x: Number(forced?.x || 0) * tileSize,
      y: Number(forced?.y || 0) * tileSize
    };
  }

  const mode = renderMode || normalizeTerrainRenderMode(resolvedTerrain);

  if (mode === 'autotile') {
    const mask = calculateTerrainBitmask({
      x,
      y,
      layer,
      terrainType,
      currentTiles,
      width,
      height,
      terrainById,
      getNeighborTerrainType
    });
    return { x: mask * tileSize, y: (resolvedTerrain.autotileRow || 0) * tileSize };
  }

  if (mode === 'hybrid' && cells.length > 0) {
    let chosenIndex = 0;

    if (getNeighborTerrainType) {
      let mask = 0;
      if (terrainHasCompatibleNeighbor(resolvedTerrain, terrainType, getNeighborTerrainType(x, y - 1, layer), terrainById)) mask += 1;
      if (terrainHasCompatibleNeighbor(resolvedTerrain, terrainType, getNeighborTerrainType(x + 1, y, layer), terrainById)) mask += 2;
      if (terrainHasCompatibleNeighbor(resolvedTerrain, terrainType, getNeighborTerrainType(x, y + 1, layer), terrainById)) mask += 4;
      if (terrainHasCompatibleNeighbor(resolvedTerrain, terrainType, getNeighborTerrainType(x - 1, y, layer), terrainById)) mask += 8;
      chosenIndex = mask % cells.length;
    } else {
      chosenIndex = Math.abs((x * 31) + (y * 17) + Number(terrainType) + Number(variantSeed || 0)) % cells.length;
    }

    const variant = cells[chosenIndex];
    return {
      x: Number(variant?.x || 0) * tileSize,
      y: Number(variant?.y || 0) * tileSize
    };
  }

  return resolveTerrainFrame(resolvedTerrain, tileSize);
}

export function recommendTerrainRenderMode(terrain) {
  const maskMap = terrain?.maskMap || 'default';
  if (terrain?.renderMode) return normalizeTerrainRenderMode(terrain);
  if (['water', 'road', 'wall', 'forest', 'swamp', 'lake'].includes(maskMap)) return 'autotile';
  if (maskMap === 'beach') return 'hybrid';
  if ((terrain?.cells || []).length > 0) return 'hybrid';
  return 'fixed';
}
